import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Navbar from "../../components/Navbar/Navbar";
import "./Scheduler.css";

const PRESETS = [
  { label: "Last 1 Week", days: 7 },
  { label: "Last 2 Weeks", days: 14 },
  { label: "Last 1 Month", days: 30 },
  { label: "Last 3 Months", days: 90 },
  { label: "Last 6 Months", days: 180 },
];

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const toInputDate = (d) => d.toISOString().split("T")[0];

const Scheduler = () => {
  const today = toInputDate(new Date());
  const [startDate, setStartDate] = useState(
    toInputDate(new Date(Date.now() - 7 * 86400000))
  );
  const [endDate, setEndDate] = useState(today);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [expandedJob, setExpandedJob] = useState(null);

  const [syncRuns, setSyncRuns] = useState([]);
  const [syncRunDetail, setSyncRunDetail] = useState({});
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncError, setSyncError] = useState("");
  const [syncSuccess, setSyncSuccess] = useState("");
  const [expandedSyncId, setExpandedSyncId] = useState(null);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await axios.get("/scheduler/jobs");
      setJobs(res.data);
    } catch (err) {
      console.error("Failed to fetch jobs:", err);
    }
  }, []);

  const fetchSyncRuns = useCallback(async () => {
    try {
      const res = await axios.get("/scheduler/ticket-sync/runs");
      setSyncRuns(res.data);
    } catch (err) {
      console.error("Failed to fetch ticket sync runs:", err);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
    fetchSyncRuns();
  }, [fetchJobs, fetchSyncRuns]);

  useEffect(() => {
    const hasActive = jobs.some(
      (j) => j.status === "running" || j.status === "pending"
    );
    if (!hasActive) return;

    const interval = setInterval(fetchJobs, 3000);
    return () => clearInterval(interval);
  }, [jobs, fetchJobs]);

  useEffect(() => {
    const syncActive = syncRuns.some((r) => r.status === "running");
    if (!syncActive) return;

    const interval = setInterval(fetchSyncRuns, 3000);
    return () => clearInterval(interval);
  }, [syncRuns, fetchSyncRuns]);

  useEffect(() => {
    if (!expandedSyncId) return;
    const run = syncRuns.find((r) => r.id === expandedSyncId);
    if (!run || run.status !== "running") return;

    const poll = setInterval(async () => {
      try {
        const res = await axios.get(
          `/scheduler/ticket-sync/runs/${expandedSyncId}`
        );
        setSyncRunDetail((prev) => ({
          ...prev,
          [expandedSyncId]: res.data,
        }));
      } catch (err) {
        console.error(err);
      }
    }, 2500);
    return () => clearInterval(poll);
  }, [expandedSyncId, syncRuns]);

  const handlePreset = (days) => {
    const end = new Date();
    const start = new Date(Date.now() - days * 86400000);
    setStartDate(toInputDate(start));
    setEndDate(toInputDate(end));
  };

  const handleRunJob = async () => {
    setError("");
    setSuccess("");

    if (!startDate || !endDate) {
      setError("Please select both start and end dates.");
      return;
    }
    if (startDate > endDate) {
      setError("Start date must be before end date.");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post("/scheduler/run", {
        start_date: startDate,
        end_date: endDate,
      });
      setSuccess(`Job ${res.data.job_id} started successfully.`);
      await fetchJobs();
    } catch (err) {
      setError(
        err.response?.data?.error || "Failed to start job. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const isRunning = jobs.some(
    (j) => j.status === "running" || j.status === "pending"
  );

  const syncRunning = syncRuns.some((r) => r.status === "running");

  const handleRunTicketSync = async () => {
    setSyncError("");
    setSyncSuccess("");
    setSyncLoading(true);
    try {
      const res = await axios.post("/scheduler/ticket-sync/run");
      setSyncSuccess(`Sync ${res.data.run_id} started.`);
      await fetchSyncRuns();
    } catch (err) {
      setSyncError(
        err.response?.data?.error || "Could not start sync. Try again."
      );
    } finally {
      setSyncLoading(false);
    }
  };

  const toggleSyncLogs = async (runId) => {
    if (expandedSyncId === runId) {
      setExpandedSyncId(null);
      return;
    }
    setExpandedSyncId(runId);
    if (!syncRunDetail[runId]) {
      try {
        const res = await axios.get(`/scheduler/ticket-sync/runs/${runId}`);
        setSyncRunDetail((prev) => ({ ...prev, [runId]: res.data }));
      } catch (err) {
        console.error(err);
      }
    }
  };

  return (
    <div>
      <Navbar />
      <div className="scheduler-container">
        <div className="scheduler-header">
          <h1>Ticket Data Scheduler</h1>
          <p>
            Hourly Salesforce incremental sync, on-demand sync, and date-range
            analysis jobs.
          </p>
        </div>

        <div className="scheduler-card">
          <h2>Incremental ticket sync (Salesforce)</h2>
          <p className="scheduler-help">
            The <code>ticket_update_scheduler</code> service runs this same job
            every hour (systemd: <code>support-chat-scheduler</code>). Execution
            history is stored so you can review logs here. Only one sync runs at
            a time.
          </p>
          <div className="date-form">
            <button
              className="run-btn"
              onClick={handleRunTicketSync}
              disabled={syncLoading || syncRunning}
            >
              {syncLoading
                ? "Starting…"
                : syncRunning
                ? "Sync running…"
                : "Run sync now"}
            </button>
          </div>
          {syncError && <p className="error-msg">{syncError}</p>}
          {syncSuccess && <p className="success-msg">{syncSuccess}</p>}

          <h3 style={{ marginTop: 24, marginBottom: 12, fontSize: "1rem" }}>
            Sync history
          </h3>
          {syncRuns.length === 0 ? (
            <p className="no-jobs" style={{ padding: "24px 0" }}>
              No runs recorded yet. Runs appear after the hourly job or a manual
              sync.
            </p>
          ) : (
            <table className="jobs-table">
              <thead>
                <tr>
                  <th>Run ID</th>
                  <th>Trigger</th>
                  <th>Status</th>
                  <th>Started</th>
                  <th>Completed</th>
                  <th>Logs</th>
                </tr>
              </thead>
              <tbody>
                {syncRuns.map((run) => (
                  <React.Fragment key={run.id}>
                    <tr>
                      <td style={{ fontFamily: "monospace", fontWeight: 600 }}>
                        {run.id}
                      </td>
                      <td>{run.trigger}</td>
                      <td>
                        <span
                          className={`status-badge status-${run.status}`}
                        >
                          {run.status}
                        </span>
                      </td>
                      <td>{formatDate(run.started_at)}</td>
                      <td>{formatDate(run.ended_at)}</td>
                      <td>
                        <button
                          type="button"
                          className="log-toggle-btn"
                          onClick={() => toggleSyncLogs(run.id)}
                        >
                          {expandedSyncId === run.id ? "Hide" : "View"}
                        </button>
                      </td>
                    </tr>
                    {expandedSyncId === run.id && (
                      <tr>
                        <td colSpan="6" style={{ padding: 0 }}>
                          <div className="log-panel">
                            {syncRunDetail[run.id]?.error && (
                              <p style={{ color: "#fc8181" }}>
                                {syncRunDetail[run.id].error}
                              </p>
                            )}
                            {syncRunDetail[run.id]?.log_lines?.length > 0 ? (
                              syncRunDetail[run.id].log_lines.map((line, i) => (
                                <p key={i}>{line}</p>
                              ))
                            ) : (
                              <p>
                                {run.status === "running"
                                  ? "Logs will appear as the run progresses…"
                                  : "No log lines captured."}
                              </p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="scheduler-card">
          <h2>Run New Job</h2>
          <div className="date-form">
            <div className="date-field">
              <label>Start Date</label>
              <input
                type="date"
                value={startDate}
                max={today}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="date-field">
              <label>End Date</label>
              <input
                type="date"
                value={endDate}
                max={today}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <button
              className="run-btn"
              onClick={handleRunJob}
              disabled={loading || isRunning}
            >
              {loading
                ? "Starting..."
                : isRunning
                ? "Job Running..."
                : "Run Job"}
            </button>
          </div>
          <div className="presets">
            {PRESETS.map((p) => (
              <button
                key={p.days}
                className="preset-btn"
                onClick={() => handlePreset(p.days)}
              >
                {p.label}
              </button>
            ))}
          </div>
          {error && <p className="error-msg">{error}</p>}
          {success && <p className="success-msg">{success}</p>}
        </div>

        <div className="scheduler-card">
          <h2>Job History</h2>
          {jobs.length === 0 ? (
            <p className="no-jobs">
              No jobs yet. Select a date range and run your first job.
            </p>
          ) : (
            <table className="jobs-table">
              <thead>
                <tr>
                  <th>Job ID</th>
                  <th>Date Range</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Tickets</th>
                  <th>Started</th>
                  <th>Completed</th>
                  <th>Logs</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <React.Fragment key={job.id}>
                    <tr>
                      <td style={{ fontFamily: "monospace", fontWeight: 600 }}>
                        {job.id}
                      </td>
                      <td>
                        {job.start_date} → {job.end_date}
                      </td>
                      <td>
                        <span className={`status-badge status-${job.status}`}>
                          {job.status}
                        </span>
                      </td>
                      <td style={{ minWidth: 120 }}>
                        <div className="progress-bar-container">
                          <div
                            className={`progress-bar-fill ${job.status}`}
                            style={{ width: `${job.progress}%` }}
                          />
                        </div>
                        <span className="progress-text">{job.progress}%</span>
                      </td>
                      <td>
                        <span title="Processed">{job.processed_tickets}</span>
                        {job.skipped_tickets > 0 && (
                          <span title="Skipped (no comments)" style={{ color: "#a0aec0" }}> +{job.skipped_tickets}s</span>
                        )}
                        {job.error_tickets > 0 && (
                          <span title="Errors" style={{ color: "#e53e3e" }}> / {job.error_tickets}err</span>
                        )}
                        <span style={{ color: "#718096" }}> / {job.total_tickets}</span>
                      </td>
                      <td>{formatDate(job.started_at)}</td>
                      <td>{formatDate(job.completed_at)}</td>
                      <td>
                        <button
                          className="log-toggle-btn"
                          onClick={() =>
                            setExpandedJob(
                              expandedJob === job.id ? null : job.id
                            )
                          }
                        >
                          {expandedJob === job.id ? "Hide" : "View"}
                        </button>
                      </td>
                    </tr>
                    {expandedJob === job.id && (
                      <tr>
                        <td colSpan="8" style={{ padding: 0 }}>
                          <div className="log-panel">
                            {job.logs && job.logs.length > 0 ? (
                              job.logs.map((log, i) => <p key={i}>{log}</p>)
                            ) : (
                              <p>No logs yet.</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Scheduler;
